<template>
  <div>
    <div class="fin-card">
      <div class="fin-range-row">
        <div class="fin-field">
          <label>Periudha</label>
          <div class="fin-quick-ranges">
            <button type="button" v-for="option in quickRanges" :key="option.key" :class="{ active: activeQuickRange === option.key }" @click="applyQuickRange(option.key)">
              {{ option.label }}
            </button>
          </div>
        </div>

        <div class="fin-field">
          <label>Nga</label>
          <input type="datetime-local" class="fin-input" :value="fromInput" @change="onFromChange($event.target.value)" />
        </div>

        <div class="fin-field">
          <label>Deri</label>
          <input type="datetime-local" class="fin-input" :value="toInput" @change="onToChange($event.target.value)" />
        </div>

        <div class="fin-field">
          <label>Lloji i lëvizjes</label>
          <div class="fin-segmented">
            <button type="button" :class="{ active: direction === 'in' }" @click="$emit('update:direction', 'in')">Furnizime</button>
            <button type="button" :class="{ active: direction === 'out' }" @click="$emit('update:direction', 'out')">Dalje</button>
            <button type="button" :class="{ active: direction === 'all' }" @click="$emit('update:direction', 'all')">Të gjitha</button>
          </div>
        </div>

        <div class="fin-field">
          <label>Rritje minimale</label>
          <input class="fin-input" type="number" min="0" step="1" :value="minIncrease" @change="$emit('update:minIncrease', Number($event.target.value) || 0)" placeholder="p.sh. 10" />
        </div>
      </div>
      <p class="fin-hint" style="margin: 10px 0 0">
        Furnizimet janë neti i ditës: shkruhen një herë në mbrëmje, jo çdo 5 minuta. Rifresko nga financa përditëson vetëm gjendjen.
      </p>
    </div>

    <div class="fin-summary-row">
      <div class="fin-summary-card" :class="{ 'is-loading': loading || totalsLoading }">
        <div class="fin-summary-icon" style="background: #ecfdf5; color: #047857"><i class="fa fa-arrow-down"></i></div>
        <div>
          <div class="fin-summary-value">
            <span v-if="totalsLoading || (loading && totals.in_qty == null)" class="fin-skeleton-line fin-skeleton-line--md"></span>
            <template v-else>{{ formatQty(totals.in_qty) }}</template>
          </div>
          <div class="fin-summary-label">Sasi e hyrë ({{ totals.in_value == null ? '…' : formatValue(totals.in_value) }} vlerë)</div>
        </div>
      </div>
      <div class="fin-summary-card" :class="{ 'is-loading': loading || totalsLoading }">
        <div class="fin-summary-icon" style="background: #fef2f2; color: #b91c1c"><i class="fa fa-arrow-up"></i></div>
        <div>
          <div class="fin-summary-value">
            <span v-if="totalsLoading || (loading && totals.out_qty == null)" class="fin-skeleton-line fin-skeleton-line--md"></span>
            <template v-else>{{ formatQty(totals.out_qty) }}</template>
          </div>
          <div class="fin-summary-label">Sasi e dalë ({{ totals.out_value == null ? '…' : formatValue(totals.out_value) }} vlerë)</div>
        </div>
      </div>
      <div class="fin-summary-card" :class="{ 'is-loading': loading || totalsLoading }">
        <div class="fin-summary-icon"><i class="fa fa-boxes-stacked"></i></div>
        <div>
          <div class="fin-summary-value">
            <span v-if="totalsLoading || (loading && totals.products == null)" class="fin-skeleton-line fin-skeleton-line--sm"></span>
            <template v-else>{{ totals.products != null ? totals.products : '…' }}</template>
          </div>
          <div class="fin-summary-label">Produkte me lëvizje</div>
        </div>
      </div>
    </div>

    <financa-table-viewport :is-reloading="loading && rows.length">
        <table class="fin-table" :class="{ 'is-grouped': isGrouped }">
          <thead>
            <tr>
              <th class="fin-col-product" :rowspan="isGrouped ? 3 : 2">Produkti</th>
              <th
                v-for="group in displayGroups"
                :key="group.id"
                class="fin-col-group"
                :class="{
                  'fin-col-highlighted': isLeafHighlighted(group),
                  'fin-col-clickable': isMagazineColumn(group),
                }"
                :colspan="groupColspan(group)"
                :title="columnHeaderTitle(group)"
                @click="toggleColumnHighlight(group)"
              >
                {{ group.name }}
              </th>
              <th class="fin-col-group fin-total-cell" :colspan="subColumnCount">Totali</th>
            </tr>
            <tr v-if="isGrouped">
              <template v-for="group in displayGroups" :key="group.id + '-wh'">
                <th
                  v-for="leaf in group.children"
                  :key="leaf.id"
                  class="fin-col-group"
                  :class="{
                    'fin-col-highlighted': isLeafHighlighted(leaf),
                    'fin-col-clickable': isMagazineColumn(leaf),
                    'fin-total-cell': leaf.is_group_total,
                  }"
                  :colspan="subColumnCount"
                  :title="columnHeaderTitle(leaf)"
                  @click="toggleColumnHighlight(leaf)"
                >
                  {{ leaf.name }}
                </th>
              </template>
              <th v-if="showIn" class="fin-col-group fin-total-cell">Hyrje</th>
              <th v-if="showOut" class="fin-total-cell" :class="{ 'fin-col-group': !showIn }">Dalje</th>
            </tr>
            <tr>
              <template v-for="leaf in leafColumns" :key="leaf.id + '-sub'">
                <th
                  v-if="showIn"
                  class="fin-col-group"
                  :class="{
                    'fin-col-highlighted': isLeafHighlighted(leaf),
                    'fin-col-clickable': isMagazineColumn(leaf),
                    'fin-total-cell': leaf.is_group_total,
                  }"
                  :title="columnHeaderTitle(leaf)"
                  @click="toggleColumnHighlight(leaf)"
                >
                  Hyrje
                </th>
                <th
                  v-if="showOut"
                  :class="{
                    'fin-col-group': !showIn,
                    'fin-col-highlighted': isLeafHighlighted(leaf),
                    'fin-col-clickable': isMagazineColumn(leaf),
                    'fin-total-cell': leaf.is_group_total,
                  }"
                  :title="columnHeaderTitle(leaf)"
                  @click="toggleColumnHighlight(leaf)"
                >
                  Dalje
                </th>
              </template>
              <th v-if="showIn" class="fin-col-group fin-total-cell">Hyrje</th>
              <th v-if="showOut" class="fin-total-cell" :class="{ 'fin-col-group': !showIn }">Dalje</th>
            </tr>
          </thead>

          <tbody v-if="loading && !rows.length">
            <tr v-for="n in 8" :key="'sk-' + n" class="fin-skeleton-row">
              <td class="fin-col-product">
                <div class="fin-skeleton-line fin-skeleton-line--lg"></div>
                <div class="fin-skeleton-line fin-skeleton-line--sm" style="margin-top: 6px"></div>
              </td>
              <td v-for="i in Math.max(2, leafColumns.length * subColumnCount + subColumnCount)" :key="'skc-' + n + '-' + i" style="text-align: center">
                <span class="fin-skeleton-line fin-skeleton-line--xs"></span>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr v-for="row in rows" :key="row.code" style="cursor: pointer" title="Kliko për historikun e produktit" @click="$emit('open-history', row)">
              <td class="fin-col-product">
                <div class="fin-product-name-row">
                  <div class="fin-product-name">{{ row.name }}</div>
                  <button
                    v-if="canOpenProduct && row.guid"
                    type="button"
                    class="fin-product-kartela-btn"
                    title="Hap kartelën e produktit"
                    @click.stop="$emit('open-product', row)"
                  >
                    <i class="fa fa-pencil"></i>
                  </button>
                </div>
                <div class="fin-product-meta">{{ row.code }}<span v-if="row.barcode"> · {{ row.barcode }}</span></div>
              </td>
              <template v-for="leaf in leafColumns" :key="row.code + leaf.id">
                <td v-if="showIn" class="fin-col-group" :class="{ 'fin-col-highlighted': isLeafHighlighted(leaf), 'fin-total-cell': leaf.is_group_total }" style="text-align: center">
                  <span :class="cell(row, leaf, 'in_qty') ? 'fin-cell-in' : 'fin-cell-zero'">{{ cell(row, leaf, "in_qty") ? "+" + formatQty(cell(row, leaf, "in_qty")) : "0" }}</span>
                </td>
                <td v-if="showOut" style="text-align: center" :class="{ 'fin-col-group': !showIn, 'fin-col-highlighted': isLeafHighlighted(leaf), 'fin-total-cell': leaf.is_group_total }">
                  <span :class="cell(row, leaf, 'out_qty') ? 'fin-cell-out' : 'fin-cell-zero'">{{ cell(row, leaf, "out_qty") ? "-" + formatQty(cell(row, leaf, "out_qty")) : "0" }}</span>
                </td>
              </template>
              <td v-if="showIn" class="fin-col-group fin-total-cell fin-cell-in" style="text-align: center">
                {{ row.total.in_qty ? "+" + formatQty(row.total.in_qty) : "0" }}
              </td>
              <td v-if="showOut" class="fin-total-cell fin-cell-out" style="text-align: center" :class="{ 'fin-col-group': !showIn }">
                {{ row.total.out_qty ? "-" + formatQty(row.total.out_qty) : "0" }}
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="loading && rows.length" class="fin-table-overlay" aria-hidden="true"></div>
        <div v-else-if="!loading && !rows.length" class="fin-empty">Asnjë lëvizje e regjistruar në këtë periudhë.</div>

      <template #footer>
        <pagination-bar :pagination="pagination" @page="$emit('page', $event)" @per-page="$emit('per-page', $event)" />
      </template>
    </financa-table-viewport>
  </div>
</template>

<script>
import PaginationBar from "./PaginationBar.vue";
import FinancaTableViewport from "./FinancaTableViewport.vue";
import formatMixin from "../shared/formatMixin.js";
import columnHighlightMixin from "../shared/columnHighlightMixin.js";

export default {
  name: "FinancaSupplyView",
  components: { PaginationBar, FinancaTableViewport },
  mixins: [formatMixin, columnHighlightMixin],
  props: {
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    totals: { type: Object, default: () => ({}) },
    pagination: { type: Object, default: () => ({}) },
    range: { type: Object, required: true },
    direction: { type: String, default: "in" },
    minIncrease: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    totalsLoading: { type: Boolean, default: false },
    canOpenProduct: { type: Boolean, default: false },
  },
  emits: ["update:range", "update:direction", "update:minIncrease", "page", "per-page", "open-history", "open-product"],
  data() {
    return {
      quickRanges: [
        { key: "today", label: "Sot" },
        { key: "24h", label: "24 orë" },
        { key: "7d", label: "7 ditë" },
        { key: "30d", label: "30 ditë" },
      ],
      activeQuickRange: "24h",
    };
  },
  computed: {
    showIn() {
      return this.direction === "in" || this.direction === "all";
    },
    showOut() {
      return this.direction === "out" || this.direction === "all";
    },
    subColumnCount() {
      return (this.showIn ? 1 : 0) + (this.showOut ? 1 : 0);
    },
    isGrouped() {
      return (this.columns || []).some((column) => Array.isArray(column.children) && column.children.length);
    },
    displayGroups() {
      if (this.isGrouped) return this.columns;
      return (this.columns || []).map((column) => ({ ...column, children: [column] }));
    },
    leafColumns() {
      return this.displayGroups.flatMap((group) => group.children || [group]);
    },
    fromInput() {
      return this.toInputValue(this.range.from);
    },
    toInput() {
      return this.toInputValue(this.range.to);
    },
  },
  methods: {
    groupColspan(group) {
      return (group.children || [group]).length * this.subColumnCount;
    },
    cell(row, column, key) {
      return (row.cells[column.id] || {})[key] || 0;
    },
    toInputValue(value) {
      if (!value) return "";
      return String(value).replace(" ", "T").slice(0, 16);
    },
    toApiValue(value) {
      return value ? value.replace("T", " ") + ":00" : null;
    },
    onFromChange(value) {
      this.activeQuickRange = null;
      this.$emit("update:range", { from: this.toApiValue(value), to: this.range.to });
    },
    onToChange(value) {
      this.activeQuickRange = null;
      this.$emit("update:range", { from: this.range.from, to: this.toApiValue(value) });
    },
    applyQuickRange(key) {
      this.activeQuickRange = key;
      const to = new Date();
      const from = new Date(to.getTime());

      if (key === "today") {
        from.setHours(0, 0, 0, 0);
      } else if (key === "24h") {
        from.setTime(to.getTime() - 24 * 3600 * 1000);
      } else if (key === "7d") {
        from.setTime(to.getTime() - 7 * 24 * 3600 * 1000);
      } else if (key === "30d") {
        from.setTime(to.getTime() - 30 * 24 * 3600 * 1000);
      }

      this.$emit("update:range", {
        from: this.toApiValue(this.toLocalInput(from)),
        to: this.toApiValue(this.toLocalInput(to)),
      });
    },
  },
};
</script>
